import React, { useState, useEffect, useRef } from 'react';
import { productsAPI, api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SimpleModal } from '@/components/SimpleModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/Toast';
import { Plus, Edit2, Trash2, Package, Search, Calculator, FileText, Printer, Upload, FileSpreadsheet, Download, Loader2, Sparkles } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { EtiquetaModal } from '@/components/EtiquetaModal';
import { ImportWizard } from '@/components/ImportWizard';
import { NcmBulkAI } from '@/components/NcmBulkAI';

export const ProductsPage = () => {
  const toast = useToast();
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name } — abre o modal de confirmação
  const [showEtiquetaModal, setShowEtiquetaModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showNcmBulk, setShowNcmBulk] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [ncmSearch, setNcmSearch] = useState('');
  const [ncmResults, setNcmResults] = useState([]);
  const [ncmAiResults, setNcmAiResults] = useState([]);
  const [ncmAiLoading, setNcmAiLoading] = useState(false);
  const [fiscalData, setFiscalData] = useState({
    csts: [],
    cfops: [],
    origens: [],
    unidades: []
  });

  // Edição em lote (selecionar vários produtos e aplicar NCM/CFOP/CST de uma vez)
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkForm, setBulkForm] = useState({ ncm: '', cfop: '', cst: '', csosn: '', cest: '' });
  const [savingBulk, setSavingBulk] = useState(false);
  const [corrigindoTrib, setCorrigindoTrib] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    preco_atacado: '',
    qtd_minima_atacado: '6',
    preco_noturno: '',
    cost: '',
    stock: '',
    category: '',
    barcode: '',
    codigo_balanca: '',
    tipo_venda: 'unidade',
    image: '',
    // Campos Fiscais
    ncm: '',
    cfop: '5102',
    origem: 0,
    cst: '00',
    csosn: '102',
    cest: '',
    pis_cst: '',
    cofins_cst: '',
    aliquota_icms: 18,
    reducao_bc_icms: 0,
    aliquota_ibs: 8.8,
    aliquota_cbs: 3.65,
    unidade: 'UN'
  });

  useEffect(() => {
    loadProducts();
    loadFiscalData();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await productsAPI.getAll();
      setProducts(response.data);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    }
  };

  const loadFiscalData = async () => {
    try {
      const [cstRes, cfopRes, origensRes, unidadesRes] = await Promise.all([
        api.get('/fiscal/cst'),
        api.get('/fiscal/cfop?tipo=saida'),
        api.get('/fiscal/origens'),
        api.get('/fiscal/unidades')
      ]);
      
      setFiscalData({
        csts: cstRes.data.csts || [],
        cfops: cfopRes.data.cfops || [],
        origens: origensRes.data.origens || [],
        unidades: unidadesRes.data.unidades || []
      });
    } catch (error) {
      console.error('Erro ao carregar dados fiscais:', error);
    }
  };

  const searchNCM = async () => {
    if (ncmSearch.length < 2) {
      toast.error('Digite pelo menos 2 caracteres para buscar');
      return;
    }
    try {
      const response = await api.get(`/fiscal/ncm?q=${encodeURIComponent(ncmSearch)}`);
      setNcmResults(response.data.ncms || []);
      if (response.data.ncms?.length === 0) {
        toast.info('Nenhum NCM encontrado');
      }
    } catch (error) {
      toast.error('Erro ao buscar NCM');
    }
  };

  const selectNCM = (ncm) => {
    setFormData({
      ...formData,
      ncm: ncm.ncm,
      aliquota_icms: ncm.aliquota_icms_media ?? formData.aliquota_icms,
      aliquota_ibs: ncm.aliquota_ibs ?? formData.aliquota_ibs,
      aliquota_cbs: ncm.aliquota_cbs ?? formData.aliquota_cbs
    });
    setNcmResults([]);
    setNcmSearch('');
    setNcmAiResults([]);
    toast.success(ncm.catalogado === false
      ? `NCM ${ncm.ncm} selecionado - confira as alíquotas`
      : `NCM ${ncm.ncm} selecionado - alíquotas preenchidas automaticamente`);
  };

  const sugerirNCMComIA = async () => {
    if (!formData.name || formData.name.trim().length < 2) {
      toast.error('Preencha o nome do produto na aba Básico primeiro');
      return;
    }
    setNcmAiLoading(true);
    setNcmAiResults([]);
    try {
      const r = await api.post('/fiscal/ncm/sugerir', {
        nome: formData.name,
        descricao: formData.description || undefined,
        categoria: formData.category || undefined,
      });
      setNcmAiResults(r.data.sugestoes || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Não foi possível sugerir o NCM');
    } finally {
      setNcmAiLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        preco_atacado: formData.preco_atacado ? parseFloat(formData.preco_atacado) : null,
        qtd_minima_atacado: parseInt(formData.qtd_minima_atacado) || 6,
        preco_noturno: formData.preco_noturno ? parseFloat(formData.preco_noturno) : null,
        cost: parseFloat(formData.cost) || 0,
        stock: parseInt(formData.stock) || 0,
        origem: parseInt(formData.origem),
        aliquota_icms: parseFloat(formData.aliquota_icms) || 0,
        reducao_bc_icms: parseFloat(formData.reducao_bc_icms) || 0,
        aliquota_ibs: parseFloat(formData.aliquota_ibs) || 0,
        aliquota_cbs: parseFloat(formData.aliquota_cbs) || 0
      };

      if (editingProduct) {
        await productsAPI.update(editingProduct.id, data);
        toast.success('Produto atualizado com sucesso!');
      } else {
        await productsAPI.create(data);
        toast.success('Produto criado com sucesso!');
      }

      setShowModal(false);
      resetForm();
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar produto');
    }
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      preco_atacado: '',
      qtd_minima_atacado: '6',
      preco_noturno: '',
      cost: '',
      stock: '',
      category: '',
      barcode: '',
      codigo_balanca: '',
      tipo_venda: 'unidade',
      image: '',
      ncm: '',
      cfop: '5102',
      origem: 0,
      cst: '00',
      csosn: '102',
      cest: '',
      pis_cst: '',
      cofins_cst: '',
      aliquota_icms: 18,
      reducao_bc_icms: 0,
      aliquota_ibs: 8.8,
      aliquota_cbs: 3.65,
      unidade: 'UN'
    });
    setNcmResults([]);
    setNcmSearch('');
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || '',
      price: product.price.toString(),
      preco_atacado: product.preco_atacado ? product.preco_atacado.toString() : '',
      qtd_minima_atacado: (product.qtd_minima_atacado || 6).toString(),
      preco_noturno: product.preco_noturno ? product.preco_noturno.toString() : '',
      cost: (product.cost || 0).toString(),
      stock: (product.stock || 0).toString(),
      category: product.category,
      barcode: product.barcode || '',
      codigo_balanca: product.codigo_balanca || '',
      tipo_venda: product.tipo_venda || 'unidade',
      image: product.image || '',
      ncm: product.ncm || '',
      cfop: product.cfop || '5102',
      origem: product.origem || 0,
      cst: product.cst || '00',
      csosn: product.csosn || '102',
      cest: product.cest || '',
      pis_cst: product.pis_cst || '',
      cofins_cst: product.cofins_cst || '',
      aliquota_icms: product.aliquota_icms || 18,
      reducao_bc_icms: product.reducao_bc_icms || 0,
      aliquota_ibs: product.aliquota_ibs || 8.8,
      aliquota_cbs: product.aliquota_cbs || 3.65,
      unidade: product.unidade || 'UN'
    });
    setShowModal(true);
  };

  const handleAutoTributacao = async () => {
    try {
      const res = await api.get(`/products/sugerir-tributacao${formData.ncm ? `?ncm=${encodeURIComponent(formData.ncm)}` : ''}`);
      const t = res.data;
      setFormData((prev) => ({
        ...prev,
        cfop: t.cfop || prev.cfop,
        origem: t.origem ?? prev.origem,
        cst: t.simples ? prev.cst : (t.cst || prev.cst),
        csosn: t.simples ? (t.csosn || prev.csosn) : prev.csosn,
        pis_cst: t.pis_cst || prev.pis_cst,
        cofins_cst: t.cofins_cst || prev.cofins_cst,
        aliquota_icms: t.simples ? 0 : (t.aliquota_icms ?? prev.aliquota_icms),
        aliquota_ibs: t.aliquota_ibs ?? prev.aliquota_ibs,
        aliquota_cbs: t.aliquota_cbs ?? prev.aliquota_cbs,
      }));
      toast.success(`Tributação preenchida (${t.simples ? 'Simples Nacional' : 'Regime Normal'})`);
      if (t.observacao) toast.warning(t.observacao, { duration: 9000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao sugerir tributação');
    }
  };

  const handleCorrigirTributacaoTodos = async () => {
    if (!window.confirm('Preencher a tributação (CFOP, CST/CSOSN, PIS/COFINS) de todos os produtos com base no seu regime tributário? Campos já preenchidos serão mantidos.')) return;
    try {
      setCorrigindoTrib(true);
      const res = await api.post('/products/corrigir-tributacao', { sobrescrever: false });
      const d = res.data;
      toast.success(`${d.atualizados} produto(s) atualizado(s) — ${d.simples ? 'Simples Nacional' : 'Regime Normal'}.`);
      if (d.total_revisar > 0) {
        toast.warning(`${d.total_revisar} produto(s) com NCM que costuma ser ST. Revise: Simples → CSOSN 500, Normal → CST 60 + CEST.`, { duration: 10000 });
      }
      loadProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao corrigir tributação');
    } finally {
      setCorrigindoTrib(false);
    }
  };

  const handleDelete = async (productId) => {
    // Confirma via Dialog do shadcn (window.confirm pode ser bloqueado em PWA/embed)
    const produto = products.find((p) => p.id === productId);
    setDeleteTarget({ id: productId, name: produto?.name || 'Produto' });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const productId = deleteTarget.id;
    setDeleteTarget(null);
    try {
      await productsAPI.delete(productId);
      toast.success('Produto removido com sucesso!');
      loadProducts();
    } catch (error) {
      const detalhe =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'Erro desconhecido';
      const status = error?.response?.status;
      console.error('[ProductsPage] Erro ao excluir produto:', status, detalhe, error);
      toast.error(`Erro ao remover produto${status ? ` (HTTP ${status})` : ''}: ${detalhe}`);
    }
  };

  // --- Edição em lote ---
  const toggleSelectAll = () => {
    if (selectedIds.length === products.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(products.map((p) => p.id));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const openBulkEdit = () => {
    if (selectedIds.length === 0) {
      toast.error('Selecione pelo menos 1 produto');
      return;
    }
    setBulkForm({ ncm: '', cfop: '', cst: '', csosn: '', cest: '' });
    setShowBulkEdit(true);
  };

  const submitBulkEdit = async () => {
    const updates = Object.fromEntries(
      Object.entries(bulkForm).filter(([, v]) => v && String(v).trim())
    );
    if (Object.keys(updates).length === 0) {
      toast.error('Preencha pelo menos um campo para aplicar');
      return;
    }
    setSavingBulk(true);
    try {
      const response = await api.post('/products/bulk-update', {
        product_ids: selectedIds,
        updates,
      });
      const { modified, fields_updated } = response.data || {};
      toast.success(
        `✅ ${modified} produto(s) atualizado(s) (${fields_updated?.join(', ') || 'campos'})`
      );
      setShowBulkEdit(false);
      setSelectedIds([]);
      loadProducts();
    } catch (error) {
      const detalhe =
        error?.response?.data?.detail || error?.message || 'Erro desconhecido';
      toast.error(`Erro: ${detalhe}`);
    } finally {
      setSavingBulk(false);
    }
  };

  const openNewProductModal = () => {
    resetForm();
    setShowModal(true);
  };

  // Calcular impostos estimados
  const calcularImpostosEstimados = () => {
    const preco = parseFloat(formData.price) || 0;
    const icms = preco * (parseFloat(formData.aliquota_icms) || 0) / 100;
    const ibs = preco * (parseFloat(formData.aliquota_ibs) || 0) / 100;
    const cbs = preco * (parseFloat(formData.aliquota_cbs) || 0) / 100;
    return { icms, ibs, cbs, total: icms + ibs + cbs };
  };

  const impostos = calcularImpostosEstimados();

  // Importar produtos de arquivo
  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setImportLoading(true);
    setImportResult(null);
    
    try {
      const response = await productsAPI.importExcel(file);
      setImportResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} produtos importados!`);
        loadProducts();
      } else {
        toast.warning('Nenhum produto foi importado');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar arquivo');
      setImportResult({
        success: false,
        error: error.response?.data?.detail || 'Erro ao importar arquivo'
      });
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Baixar template de exemplo
  const downloadTemplate = () => {
    // Criar CSV de exemplo
    const csvContent = `nome,preco,categoria,estoque,codigo_barras,custo,ncm,unidade,preco_atacado,codigo_balanca
Coca-Cola 2L,12.00,Bebidas,50,7894900011517,8.00,22021000,UN,10.00,
Guaraná Antarctica 350ml,4.50,Bebidas,100,7891149101009,3.00,22021000,UN,4.00,
Carne Bovina (kg),45.90,Carnes,100,,35.00,02013000,KG,,0001
Arroz 5kg,24.90,Mercearia,50,7896006744016,18.00,10063021,UN,22.00,
Feijão 1kg,8.90,Mercearia,80,7896006702412,6.00,07133319,UN,8.00,`;
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'template_produtos.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Template baixado!');
  };

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="products-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Produtos</h1>
          <p className="text-slate-600">Gerencie o cadastro de produtos com informações fiscais</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowNcmBulk(true)}
            variant="outline"
            className="border-violet-500 text-violet-700 hover:bg-violet-50"
            data-testid="ncm-bulk-button"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            NCM em lote (IA)
          </Button>
          <Button
            onClick={handleCorrigirTributacaoTodos}
            variant="outline"
            className="border-amber-500 text-amber-700 hover:bg-amber-50"
            disabled={corrigindoTrib}
            data-testid="fix-taxation-button"
          >
            {corrigindoTrib ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}
            Corrigir Tributação
          </Button>
          <Button 
            onClick={() => setShowImportModal(true)} 
            variant="outline"
            className="border-blue-500 text-blue-600 hover:bg-blue-50"
            data-testid="import-products-button"
          >
            <Upload className="w-4 h-4 mr-2" />
            Importar
          </Button>
          <Button 
            onClick={() => setShowEtiquetaModal(true)} 
            variant="outline"
            className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
            data-testid="print-labels-button"
          >
            <Printer className="w-4 h-4 mr-2" />
            Etiquetas
          </Button>
          <Button onClick={openNewProductModal} className="bg-slate-900 hover:bg-slate-800" data-testid="add-product-button">
            <Plus className="w-4 h-4 mr-2" />
            Novo Produto
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
        {/* Barra de ações em lote (aparece quando há produtos selecionados) */}
        {selectedIds.length > 0 && (
          <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 flex items-center justify-between" data-testid="bulk-actions-bar">
            <span className="text-sm font-medium text-amber-900">
              <strong>{selectedIds.length}</strong> produto(s) selecionado(s)
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedIds([])}
                data-testid="bulk-clear-selection"
              >
                Limpar seleção
              </Button>
              <Button
                size="sm"
                className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={openBulkEdit}
                data-testid="bulk-edit-button"
              >
                ✏️ Editar NCM/CFOP em lote
              </Button>
            </div>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <input
                  type="checkbox"
                  checked={products.length > 0 && selectedIds.length === products.length}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 cursor-pointer"
                  data-testid="bulk-select-all"
                />
              </TableHead>
              <TableHead>Produto</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead>NCM</TableHead>
              <TableHead>Preço</TableHead>
              <TableHead>ICMS</TableHead>
              <TableHead>Estoque</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map(product => (
              <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                <TableCell>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(product.id)}
                    onChange={() => toggleSelectOne(product.id)}
                    className="w-4 h-4 cursor-pointer"
                    data-testid={`bulk-select-${product.id}`}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {product.image ? (
                      <img src={product.image} alt={product.name} className="w-12 h-12 object-cover rounded" />
                    ) : (
                      <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center">
                        <Package className="w-6 h-6 text-slate-400" />
                      </div>
                    )}
                    <div>
                      <p className="font-semibold">{product.name}</p>
                      {product.barcode && <p className="text-xs text-slate-500">{product.barcode}</p>}
                    </div>
                  </div>
                </TableCell>
                <TableCell>{product.category}</TableCell>
                <TableCell>
                  <span className="font-mono text-xs bg-slate-100 px-2 py-1 rounded">
                    {product.ncm || '-'}
                  </span>
                </TableCell>
                <TableCell className="font-semibold">{formatCurrency(product.price)}</TableCell>
                <TableCell>
                  <span className="text-xs text-slate-600">{product.aliquota_icms || 0}%</span>
                </TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    product.stock <= 10 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'
                  }`}>
                    {product.stock}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => handleEdit(product)} data-testid={`edit-product-${product.id}`}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(product.id)} className="text-rose-600" data-testid={`delete-product-${product.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="product-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingProduct ? 'Editar Produto' : 'Novo Produto'}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados do produto incluindo informações fiscais
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="basic">
                  <Package className="w-4 h-4 mr-2" />
                  Dados Básicos
                </TabsTrigger>
                <TabsTrigger value="fiscal">
                  <FileText className="w-4 h-4 mr-2" />
                  Dados Fiscais
                </TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="name">Nome *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="product-name-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="description">Descrição</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      data-testid="product-description-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">Categoria *</Label>
                    <Input
                      id="category"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      required
                      data-testid="product-category-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="barcode">Código de Barras</Label>
                    <Input
                      id="barcode"
                      value={formData.barcode}
                      onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                      data-testid="product-barcode-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="codigo_balanca">Código Balança</Label>
                    <Input
                      id="codigo_balanca"
                      value={formData.codigo_balanca}
                      onChange={(e) => setFormData({ ...formData, codigo_balanca: e.target.value })}
                      placeholder="Ex: 0123"
                      maxLength={6}
                      data-testid="product-codigo-balanca-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">Código interno da balança (4-6 dígitos)</p>
                  </div>
                  <div>
                    <Label htmlFor="tipo_venda">Tipo de Venda</Label>
                    <select
                      id="tipo_venda"
                      value={formData.tipo_venda}
                      onChange={(e) => setFormData({ ...formData, tipo_venda: e.target.value })}
                      className="w-full h-10 px-3 border border-slate-300 rounded-md bg-white"
                      data-testid="product-tipo-venda-select"
                    >
                      <option value="unidade">Unidade</option>
                      <option value="pesado">Pesado (kg)</option>
                      <option value="fracionado">Fracionado</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="price">Preço Varejo *</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      required
                      data-testid="product-price-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="preco_atacado">Preço Atacado</Label>
                    <Input
                      id="preco_atacado"
                      type="number"
                      step="0.01"
                      value={formData.preco_atacado}
                      onChange={(e) => setFormData({ ...formData, preco_atacado: e.target.value })}
                      placeholder="Opcional"
                      data-testid="product-preco-atacado-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="qtd_minima_atacado">Qtd Mín. Atacado</Label>
                    <Input
                      id="qtd_minima_atacado"
                      type="number"
                      min="1"
                      value={formData.qtd_minima_atacado}
                      onChange={(e) => setFormData({ ...formData, qtd_minima_atacado: e.target.value })}
                      placeholder="6"
                      data-testid="product-qtd-atacado-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="preco_noturno">Preço Noturno/Gelado</Label>
                    <Input
                      id="preco_noturno"
                      type="number"
                      step="0.01"
                      value={formData.preco_noturno}
                      onChange={(e) => setFormData({ ...formData, preco_noturno: e.target.value })}
                      placeholder="Opcional"
                      data-testid="product-preco-noturno-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cost">Custo</Label>
                    <Input
                      id="cost"
                      type="number"
                      step="0.01"
                      value={formData.cost}
                      onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                      data-testid="product-cost-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="stock">Estoque</Label>
                    <Input
                      id="stock"
                      type="number"
                      value={formData.stock}
                      onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                      data-testid="product-stock-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="unidade">Unidade</Label>
                    <select
                      id="unidade"
                      value={formData.unidade}
                      onChange={(e) => setFormData({ ...formData, unidade: e.target.value })}
                      className="w-full h-10 px-3 border border-slate-300 rounded-md bg-white"
                      data-testid="product-unidade-select"
                    >
                      {fiscalData.unidades.map(u => (
                        <option key={u.codigo} value={u.codigo}>{u.codigo} - {u.descricao}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="image">URL da Imagem</Label>
                    <Input
                      id="image"
                      value={formData.image}
                      onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                      placeholder="https://..."
                      data-testid="product-image-input"
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="fiscal" className="space-y-4">
                {/* NCM Search */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Buscar NCM</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Digite parte do nome do produto (ex: água, queijo, celular)"
                        value={ncmSearch}
                        onChange={(e) => setNcmSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), searchNCM())}
                        data-testid="ncm-search-input"
                      />
                      <Button type="button" onClick={searchNCM} variant="outline">
                        <Search className="w-4 h-4 mr-2" />
                        Buscar
                      </Button>
                      <Button
                        type="button"
                        onClick={sugerirNCMComIA}
                        disabled={ncmAiLoading}
                        className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white"
                        data-testid="ncm-ai-suggest-btn"
                      >
                        {ncmAiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                        Sugerir com IA
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500">A IA usa o nome e a descrição do produto para sugerir o NCM correto e evitar rejeição na SEFAZ.</p>

                    {ncmAiResults.length > 0 && (
                      <div className="border border-violet-200 bg-violet-50/50 rounded-lg divide-y divide-violet-100" data-testid="ncm-ai-results">
                        {ncmAiResults.map((s, i) => (
                          <div
                            key={s.ncm}
                            className="p-3 hover:bg-violet-100/60 cursor-pointer"
                            onClick={() => selectNCM(s)}
                            data-testid={`ncm-ai-option-${i}`}
                          >
                            <div className="flex justify-between items-start gap-3">
                              <div>
                                <span className="font-mono font-semibold">{s.ncm}</span>
                                {i === 0 && <span className="ml-2 text-[10px] uppercase tracking-wide bg-violet-600 text-white px-1.5 py-0.5 rounded">Mais provável</span>}
                                <p className="text-sm text-slate-700">{s.descricao}</p>
                                {s.motivo && <p className="text-xs text-slate-500 mt-0.5">{s.motivo}</p>}
                              </div>
                              <div className="text-right text-xs shrink-0">
                                <p className="font-medium text-violet-700">{Math.round((s.confianca || 0) * 100)}% confiança</p>
                                {s.aliquota_icms_media != null && <p>ICMS: {s.aliquota_icms_media}%</p>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {ncmResults.length > 0 && (
                      <div className="border rounded-lg max-h-48 overflow-y-auto">
                        {ncmResults.map(ncm => (
                          <div
                            key={ncm.ncm}
                            className="p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                            onClick={() => selectNCM(ncm)}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <span className="font-mono font-semibold">{ncm.ncm}</span>
                                <p className="text-sm text-slate-600">{ncm.descricao}</p>
                              </div>
                              <div className="text-right text-xs">
                                <p>ICMS: {ncm.aliquota_icms_media}%</p>
                                <p>IBS: {ncm.aliquota_ibs}%</p>
                                <p>CBS: {ncm.aliquota_cbs}%</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Separator />

                {/* Dados Fiscais */}
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-slate-800">Dados Fiscais</h4>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAutoTributacao}
                    className="border-amber-500 text-amber-700 hover:bg-amber-50"
                    data-testid="auto-taxation-button"
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Preencher automático (regime + NCM)
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="ncm">NCM (8 dígitos)</Label>
                    <Input
                      id="ncm"
                      value={formData.ncm}
                      onChange={(e) => setFormData({ ...formData, ncm: e.target.value })}
                      placeholder="00000000"
                      maxLength={8}
                      data-testid="product-ncm-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cfop">CFOP</Label>
                    <select
                      id="cfop"
                      value={formData.cfop}
                      onChange={(e) => setFormData({ ...formData, cfop: e.target.value })}
                      className="w-full h-10 px-3 border border-slate-300 rounded-md bg-white text-sm"
                      data-testid="product-cfop-select"
                    >
                      {fiscalData.cfops.map(c => (
                        <option key={c.cfop} value={c.cfop}>{c.cfop} - {c.descricao.substring(0, 40)}...</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="origem">Origem</Label>
                    <select
                      id="origem"
                      value={formData.origem}
                      onChange={(e) => setFormData({ ...formData, origem: parseInt(e.target.value) })}
                      className="w-full h-10 px-3 border border-slate-300 rounded-md bg-white text-sm"
                      data-testid="product-origem-select"
                    >
                      {fiscalData.origens.map(o => (
                        <option key={o.codigo} value={o.codigo}>{o.codigo} - {o.descricao.substring(0, 35)}...</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="cst">CST ICMS (Regime Normal)</Label>
                    <select
                      id="cst"
                      value={formData.cst}
                      onChange={(e) => setFormData({ ...formData, cst: e.target.value })}
                      className="w-full h-10 px-3 border border-slate-300 rounded-md bg-white text-sm"
                      data-testid="product-cst-select"
                    >
                      {fiscalData.csts.map(c => (
                        <option key={c.cst} value={c.cst}>{c.cst} - {c.descricao}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="csosn">CSOSN (Simples Nacional)</Label>
                    <select
                      id="csosn"
                      value={formData.csosn}
                      onChange={(e) => setFormData({ ...formData, csosn: e.target.value })}
                      className="w-full h-10 px-3 border border-slate-300 rounded-md bg-white text-sm"
                      data-testid="product-csosn-select"
                    >
                      <option value="">-- Selecione --</option>
                      <option value="101">101 - Com permissão de crédito</option>
                      <option value="102">102 - Sem permissão de crédito</option>
                      <option value="103">103 - Isenção por faixa de receita</option>
                      <option value="201">201 - Com crédito e ST</option>
                      <option value="202">202 - Sem crédito e ST</option>
                      <option value="203">203 - Isenção e ST</option>
                      <option value="300">300 - Imune</option>
                      <option value="400">400 - Não tributada</option>
                      <option value="500">500 - ICMS cobrado por ST</option>
                      <option value="900">900 - Outros</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="aliquota_icms">Alíquota ICMS (%)</Label>
                    <Input
                      id="aliquota_icms"
                      type="number"
                      step="0.01"
                      value={formData.aliquota_icms}
                      onChange={(e) => setFormData({ ...formData, aliquota_icms: e.target.value })}
                      data-testid="product-icms-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="cest">CEST (apenas se for ST)</Label>
                    <Input
                      id="cest"
                      value={formData.cest}
                      onChange={(e) => setFormData({ ...formData, cest: e.target.value })}
                      placeholder="0000000"
                      maxLength={7}
                      data-testid="product-cest-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pis_cst">CST PIS</Label>
                    <Input
                      id="pis_cst"
                      value={formData.pis_cst}
                      onChange={(e) => setFormData({ ...formData, pis_cst: e.target.value })}
                      placeholder="49"
                      maxLength={2}
                      data-testid="product-pis-cst-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cofins_cst">CST COFINS</Label>
                    <Input
                      id="cofins_cst"
                      value={formData.cofins_cst}
                      onChange={(e) => setFormData({ ...formData, cofins_cst: e.target.value })}
                      placeholder="49"
                      maxLength={2}
                      data-testid="product-cofins-cst-input"
                    />
                  </div>
                </div>

                <Separator />

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Reforma Tributária 2026 - IBS/CBS</h4>
                  <p className="text-sm text-blue-800 mb-3">
                    Alíquotas do novo sistema tributário brasileiro (IVA Dual)
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="aliquota_ibs">Alíquota IBS (%) - Estadual/Municipal</Label>
                      <Input
                        id="aliquota_ibs"
                        type="number"
                        step="0.01"
                        value={formData.aliquota_ibs}
                        onChange={(e) => setFormData({ ...formData, aliquota_ibs: e.target.value })}
                        data-testid="product-ibs-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="aliquota_cbs">Alíquota CBS (%) - Federal</Label>
                      <Input
                        id="aliquota_cbs"
                        type="number"
                        step="0.01"
                        value={formData.aliquota_cbs}
                        onChange={(e) => setFormData({ ...formData, aliquota_cbs: e.target.value })}
                        data-testid="product-cbs-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Resumo de Impostos */}
                {formData.price && (
                  <Card className="bg-slate-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Calculator className="w-4 h-4" />
                        Impostos Estimados (por unidade)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-slate-600">ICMS</p>
                          <p className="font-semibold">{formatCurrency(impostos.icms)}</p>
                        </div>
                        <div>
                          <p className="text-slate-600">IBS</p>
                          <p className="font-semibold">{formatCurrency(impostos.ibs)}</p>
                        </div>
                        <div>
                          <p className="text-slate-600">CBS</p>
                          <p className="font-semibold">{formatCurrency(impostos.cbs)}</p>
                        </div>
                        <div>
                          <p className="text-slate-600">Total Impostos</p>
                          <p className="font-bold text-lg">{formatCurrency(impostos.total)}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-product-button">
                {editingProduct ? 'Atualizar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Modal de Etiquetas */}
      <EtiquetaModal 
        open={showEtiquetaModal}
        onClose={() => setShowEtiquetaModal(false)}
        products={products}
      />

      {/* Assistente de Importação / Migração */}
      <NcmBulkAI open={showNcmBulk} onClose={() => setShowNcmBulk(false)} products={products} toast={toast} onApplied={loadProducts} />

      <ImportWizard
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImported={loadProducts}
      />

      {/* Confirmação de exclusão (substitui window.confirm) */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="confirm-delete-product-dialog">
          <DialogHeader>
            <DialogTitle>Excluir produto</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja remover <strong>{deleteTarget?.name}</strong>?
              <br />Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              data-testid="confirm-delete-product-cancel"
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              data-testid="confirm-delete-product-confirm"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edição em LOTE — aplica NCM/CFOP/CST em vários produtos de uma vez */}
      <Dialog open={showBulkEdit} onOpenChange={setShowBulkEdit}>
        <DialogContent className="max-w-lg" data-testid="bulk-edit-dialog">
          <DialogHeader>
            <DialogTitle>✏️ Edição em Lote — {selectedIds.length} produto(s)</DialogTitle>
            <DialogDescription>
              Preencha somente os campos que deseja aplicar. Campos em branco serão ignorados.
              <br />Útil para corrigir NCM/CFOP de vários produtos antes de emitir NF-e.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-700">NCM (8 dígitos)</label>
              <input
                type="text"
                maxLength={8}
                value={bulkForm.ncm}
                onChange={(e) => setBulkForm({ ...bulkForm, ncm: e.target.value.replace(/[^0-9]/g, '') })}
                placeholder="Ex: 73066100"
                className="w-full px-3 py-2 border rounded font-mono"
                data-testid="bulk-edit-ncm"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">CFOP (4 dígitos)</label>
              <input
                type="text"
                maxLength={4}
                value={bulkForm.cfop}
                onChange={(e) => setBulkForm({ ...bulkForm, cfop: e.target.value.replace(/[^0-9]/g, '') })}
                placeholder="Ex: 5102"
                className="w-full px-3 py-2 border rounded font-mono"
                data-testid="bulk-edit-cfop"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">CST</label>
                <input
                  type="text"
                  maxLength={3}
                  value={bulkForm.cst}
                  onChange={(e) => setBulkForm({ ...bulkForm, cst: e.target.value })}
                  placeholder="Ex: 00"
                  className="w-full px-3 py-2 border rounded font-mono"
                  data-testid="bulk-edit-cst"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">CSOSN</label>
                <input
                  type="text"
                  maxLength={4}
                  value={bulkForm.csosn}
                  onChange={(e) => setBulkForm({ ...bulkForm, csosn: e.target.value })}
                  placeholder="Ex: 102"
                  className="w-full px-3 py-2 border rounded font-mono"
                  data-testid="bulk-edit-csosn"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">CEST (opcional)</label>
              <input
                type="text"
                maxLength={9}
                value={bulkForm.cest}
                onChange={(e) => setBulkForm({ ...bulkForm, cest: e.target.value })}
                placeholder="Ex: 0100100"
                className="w-full px-3 py-2 border rounded font-mono"
                data-testid="bulk-edit-cest"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBulkEdit(false)}
              disabled={savingBulk}
              data-testid="bulk-edit-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={submitBulkEdit}
              disabled={savingBulk}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="bulk-edit-save"
            >
              {savingBulk ? '⏳ Aplicando...' : `Aplicar em ${selectedIds.length} produto(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
